import { Button, Space } from '@gr-monorepo/common/ui';
import { MunicipalityRatings, Olympiad } from 'features/olympiads/model';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Chart, { ChartRow } from './Chart';
import useUserData from 'hooks/useUserData';
import { LogClick, useClickStreamContext } from 'features/shared/ClickStream';
import RatingImproveResultPopup from 'features/shared/RatingImproveResultPopup';
import pluralize from 'utils/pluralize';

const GrayPlate = styled.div`
  border-radius: 16px;
  background: #f4f5f6;
  padding: 16px;
`;

const Flex = styled.div`
  display: flex;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
`;
const SubTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: rgba(9, 21, 38, 0.6);
`;

const Text = styled.div`
  color: rgba(9, 21, 38, 0.6);
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
`;

const EmptyRatingsContent = (props: { text: string; logPageEvent: (props: any) => void }) => {
  const [showImproveResultPopup, setShowImproveResultPopup] = useState(false);

  return (
    <Flex>
      <span style={{ fontSize: 14, lineHeight: '20px' }}>
        Ученики вашего муниципалитета еще не используют Учи.ру. Чтобы смотреть статистику,
        <br /> отправьте школам письмо о возможностях платформы
      </span>
      <Space />

      <Button
        color={'violet'}
        onClick={() => {
          props.logPageEvent({
            eventType: 'school_rating_empty_ratings_click_improve_result',
            eventInfo: 'Школы рейтинг пустой рейтинг: Клик по кнопке отправить',
          });
          setShowImproveResultPopup(true);
        }}
      >
        ОТПРАВИТЬ
      </Button>

      {showImproveResultPopup && (
        <RatingImproveResultPopup
          text={props.text}
          eventPrefix="school_rating_empty_ratings"
          eventTitlePrefix="Школы рейтинг пустой рейтинг"
          onClose={() => {
            setShowImproveResultPopup(false);
          }}
          headerColor={'#1CC67F'}
        />
      )}
    </Flex>
  );
};

const OlympRatings = (props: { data: Olympiad; color: string }) => {
  const userData = useUserData();

  const { logPageEvent } = useClickStreamContext();

  const [showImproveResultPopup, setShowImproveResultPopup] = useState(false);

  const isEmpty = useMemo(() => {
    return !props.data || !props.data.ratings || !props.data.ratings.some((el) => el.value !== 0);
  }, [props.data]);

  const rows = useMemo(
    () =>
      props.data.ratings
        ?.filter((el) => el.value > 0 || userData?.id === el.subject_id)
        .sort((a, b) =>
          b.value === a.value
            ? a.subject_id === userData?.id
              ? -1
              : b.subject_id === userData?.id
              ? 1
              : b.subject_name > a.subject_name
              ? -1
              : 1
            : b.value - a.value,
        )
        .map((el) => ({ title: el.subject_name, value: el.value, isCurrent: userData?.id === el.subject_id })) || [],
    [props.data],
  );
  const leaderRows = rows.slice(0, 3).filter((el) => !!el.value);

  const selfRow = rows.find((el) => el.isCurrent);
  const selfRowIndex = selfRow ? rows.indexOf(selfRow) : null;
  const isLeader = selfRow?.value! > 0 && selfRowIndex! < 3;

  return (
    <GrayPlate>
      <Title>{props.data.name}</Title>
      <Space size={24} axis="vertical" />
      {!isEmpty && (
        <>
          <Chart header={'Доля участников олимпиады'} rows={leaderRows} color={props.color} />
          <Space axis="vertical" size={32} />
        </>
      )}
      {!isLeader && selfRow ? (
        <>
          <SubTitle>Ваш текущий результат</SubTitle>
          <Space axis="vertical" size={8} />
          <ChartRow {...selfRow!} color={props.color}></ChartRow>
        </>
      ) : null}
      {!!props.data.total.participantsCount && (
        <>
          <Text>
            В вашем муниципалитете {pluralize(props.data.total.participantsCount, ['принял', 'приняли', 'приняло'])}{' '}
            <br />
            участие{' '}
            <span style={{ fontWeight: 'bold', color: 'rgba(9, 21, 38, 0.85)' }}>
              {props.data.total.participantsCount}
            </span>{' '}
            {pluralize(props.data.total.participantsCount, ['ученик', 'ученика', 'учеников'])}
          </Text>
        </>
      )}
    </GrayPlate>
  );
};

export default OlympRatings;
